import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, CircularProgress, Typography,
  Grid, Paper
} from '@mui/material';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { makeRequest } from '../../../assets/functions_helper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid, isBefore, startOfDay, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// ─── Paleta de colores ────────────────────────────────────────────────────────
const C = {
  dark:    '#0f172a',
  darkHov: '#1e293b',
  muted:   '#475569',
  border:  '#cbd5e1',
  bg:      '#f8fafc',
  white:   '#ffffff',
  accent:  '#6366f1',
  red:     '#ef4444',
  amber:   '#b45309',
};

function ReservationForm({ restaurantId, operatingHours, onSuccess }) {
  const { executeRecaptcha } = useGoogleReCaptcha();

  // ─── Datos del formulario ───────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    guest_count: '',
    date: null,
    notes: '',
  });

  // ─── Paso 1: Slots de hora ──────────────────────────────────────────────────
  const [selectedSlot,    setSelectedSlot]    = useState(null);
  const [availableSlots,  setAvailableSlots]  = useState(null);
  const [slotDuration,    setSlotDuration]    = useState(null);
  const [slotsLoading,    setSlotsLoading]    = useState(false);
  const [slotsClosed,     setSlotsClosed]     = useState(false);

  // ─── UI general ────────────────────────────────────────────────────────────
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);

  const dayIndexToKey = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

  // ─── Handlers básicos ──────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleDateChange = (newDate) => {
    setFormData(prev => ({ ...prev, date: newDate }));
    setSelectedSlot(null);
    if (errors.requested_datetime) setErrors(prev => ({ ...prev, requested_datetime: null }));
  };

  // ─── Helpers de horario ────────────────────────────────────────────────────
  const getDayConfig = (dateObj) => {
    if (!dateObj || !isValid(dateObj) || !operatingHours) return null;
    const dayKey = dayIndexToKey[getDay(dateObj)];
    const hoursData = operatingHours.operating_hours ?? operatingHours;
    return hoursData[dayKey];
  };

  const shouldDisableDate = (dateObj) => {
    const config = getDayConfig(dateObj);
    if (!config || config.closed === true) return true;
    if (isBefore(dateObj, startOfDay(new Date()))) return true;
    return false;
  };

  const formatDuration = (hours) => {
    if (!hours) return '';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    const parts = [];
    if (h > 0) parts.push(`${h} hora${h !== 1 ? 's' : ''}`);
    if (m > 0) parts.push(`${m} minutos`);
    return parts.join(' y ');
  };

  // ─── Efecto: cargar slots cuando cambia fecha o comensales ─────────────────
  // El endpoint devuelve directamente los slots DISPONIBLES.
  // • slots[] con items → mostrar esos horarios al cliente
  // • slots[] vacío + closed:false → sin disponibilidad (todo ocupado)
  // • closed:true → restaurante cerrado ese día
  useEffect(() => {
    const count = parseInt(formData.guest_count, 10);
    if (formData.date && isValid(formData.date) && count > 0) {
      const dateStr = format(formData.date, 'yyyy-MM-dd');
      setSlotsLoading(true);
      setSelectedSlot(null);
      setSlotsClosed(false);
      setAvailableSlots(null);

      makeRequest(
        'GET',
        `reservations/available-slots?restaurant_public_id=${restaurantId}&date=${dateStr}&guest_count=${count}`,
        null, 'application/json', null, null, null, true
      )
        .then(data => {
          if (data?.closed === true) {
            setSlotsClosed(true);
            setAvailableSlots([]);
          } else {
            // Usar directamente los slots que devuelve la API
            setAvailableSlots(data?.slots || []);
            setSlotDuration(data?.slot_duration_hours || null);
          }
        })
        .catch(err => {
          console.error('Error fetching slots:', err);
          setAvailableSlots([]);
        })
        .finally(() => setSlotsLoading(false));
    } else {
      setAvailableSlots(null);
      setSelectedSlot(null);
      setSlotsClosed(false);
    }
  }, [formData.date, formData.guest_count, restaurantId]);

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSlot) {
      setErrors({ general: 'Debes seleccionar una hora para la reserva.' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      let recaptchaToken = null;
      if (executeRecaptcha) recaptchaToken = await executeRecaptcha('reserve_submit');

      const payload = {
        customer_name:       formData.customer_name,
        customer_phone:      formData.customer_phone,
        guest_count:         parseInt(formData.guest_count, 10),
        requested_datetime:  selectedSlot.datetime,
        recaptcha_token:     recaptchaToken,
        notes:               formData.notes,
      };

      const response = await makeRequest(
        'POST',
        `reservations?restaurant_public_id=${restaurantId}`,
        payload, 'application/json', null, null, null, true
      );

      onSuccess(response?.reservation_id || response?.id || 'REF-OK');

    } catch (err) {
      console.error('Error submitting reservation:', err);
      if (err.details && Array.isArray(err.details)) {
        const newErrors = {};
        err.details.forEach(d => {
          const field = d.loc?.[d.loc.length - 1];
          if (field) newErrors[field] = d.msg;
        });
        setErrors(newErrors);
      } else {
        setErrors({ general: err.message || 'Ha ocurrido un error inesperado.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Render helpers ────────────────────────────────────────────────────────
  const inputSx = { borderRadius: '14px' };

  const canSubmit =
    !!formData.date && !!selectedSlot &&
    !!formData.customer_name && !!formData.customer_phone && !!formData.guest_count;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '24px', border: '1px solid #e2e8f0', bgcolor: C.white }}>
        <form onSubmit={handleSubmit}>

          {/* ── Error general ── */}
          {errors.general && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px' }} elevation={0}>
              <Typography sx={{ color: C.red, fontWeight: 600 }}>{errors.general}</Typography>
            </Paper>
          )}

          {/* ══════════════════════════════════════════
              PASO 1: Datos del cliente + Fecha
          ══════════════════════════════════════════ */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Nombre completo"
                value={formData.customer_name} onChange={handleChange('customer_name')}
                error={!!errors.customer_name} helperText={errors.customer_name}
                InputProps={{ sx: inputSx }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Teléfono" type="tel"
                value={formData.customer_phone} onChange={handleChange('customer_phone')}
                error={!!errors.customer_phone} helperText={errors.customer_phone}
                InputProps={{ sx: inputSx }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth required label="Comensales" type="number"
                inputProps={{ min: 1, max: 20 }}
                value={formData.guest_count} onChange={handleChange('guest_count')}
                error={!!errors.guest_count} helperText={errors.guest_count}
                InputProps={{ sx: inputSx }}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <DatePicker
                label="Fecha"
                value={formData.date}
                onChange={handleDateChange}
                shouldDisableDate={shouldDisableDate}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true, required: true,
                    error: !!errors.requested_datetime || !!errors.date,
                    InputProps: { sx: inputSx },
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* ══════════════════════════════════════════
              PASO 2: Horarios disponibles
          ══════════════════════════════════════════ */}
          <Box sx={{ mt: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTimeIcon sx={{ color: C.dark, fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: C.dark, mb: 0, fontFamily: 'inherit' }}>Horarios Disponibles</Typography>
            </Box>

            {slotsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : slotsClosed ? (
              <Paper sx={{ p: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px' }} elevation={0}>
                <Typography sx={{ color: C.red, fontWeight: 600 }}>El restaurante está cerrado en este día.</Typography>
              </Paper>
            ) : availableSlots !== null && availableSlots.length === 0 ? (
              <Paper sx={{ p: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px' }} elevation={0}>
                <Typography sx={{ color: C.amber, fontWeight: 600 }}>No hay disponibilidad para esta fecha. Por favor intenta otro día.</Typography>
              </Paper>
            ) : availableSlots !== null && availableSlots.length > 0 ? (
              <Grid container spacing={1.5}>
                {availableSlots.map((slot, i) => {
                  const isSelected = selectedSlot?.datetime === slot.datetime;
                  return (
                    <Grid item xs={4} sm={3} md={2} key={i}>
                      <Button
                        fullWidth
                        onClick={() => setSelectedSlot(slot)}
                        sx={{
                          borderRadius: '12px',
                          py: 1.4,
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          textTransform: 'none',
                          border: `2px solid ${isSelected ? C.dark : C.border}`,
                          bgcolor: isSelected ? C.dark : C.white,
                          color:   isSelected ? C.white : C.muted,
                          boxShadow: isSelected ? '0 4px 14px rgba(15,23,42,0.18)' : 'none',
                          transition: 'all .15s ease',
                          '&:hover': {
                            bgcolor:   isSelected ? C.darkHov : C.bg,
                            borderColor: C.dark,
                          },
                        }}
                      >
                        {slot.time}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Selecciona la cantidad de comensales y la fecha para ver los horarios.
              </Typography>
            )}
          </Box>

          {/* ══════════════════════════════════════════
              Resumen de Duración
          ══════════════════════════════════════════ */}
          {selectedSlot && slotDuration && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.5 }} elevation={0}>
              <AccessTimeIcon sx={{ color: '#16a34a' }} />
              <Typography sx={{ color: '#166534', fontWeight: 600, fontSize: '0.95rem' }}>
                Tu mesa estará reservada por un máximo de {formatDuration(slotDuration)}.
              </Typography>
            </Paper>
          )}

          {/* ══════════════════════════════════════════
              Notas adicionales
          ══════════════════════════════════════════ */}
          <Box sx={{ mt: 4 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label="Notas adicionales (Opcional)"
              placeholder="Ej. Alergias, cumpleaños, requerimientos especiales"
              value={formData.notes}
              onChange={handleChange('notes')}
              helperText="Estas notas serán visibles para el restaurante."
              InputProps={{ sx: { borderRadius: '14px' } }}
            />
          </Box>

          {/* ══════════════════════════════════════════
              Botón de envío
          ══════════════════════════════════════════ */}
          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Protegido por Google reCAPTCHA
            </Typography>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !canSubmit}
              sx={{
                bgcolor: C.dark,
                color: C.white,
                px: 5, py: 1.5,
                borderRadius: '14px',
                fontWeight: 800,
                fontSize: '1.1rem',
                textTransform: 'none',
                '&:hover': { bgcolor: C.darkHov },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Solicitud'}
            </Button>
          </Box>

        </form>
      </Paper>
    </LocalizationProvider>
  );
}

export default ReservationForm;
